import { refresh } from "../../common/refresh";

/**
 * Action for appointment details
 */
export const actions = {
  /**
   * Get appointment by id
   *
   * @param {Object} context
   * @param {Number} id
   */
  async getAppointmentById(context, id) {
    // Step 1: Check if id given is a number
    if (isNaN(id)) {
      return false;
    }

    // Step 2: Send a get request to the server
    const json = await context.dispatch("makeAuthRequest", {
      url: "appointment/get/id/" + id,
      method: "GET"
    });

    if (!("error" in json)) {
      // Step 3: Check if we get a success response.
      // If yes, add appointment to appointment details
      if (json.appointment) {
        context.commit("addDetails", json.appointment);
      } else {
        context.commit("addToQueriedIdAppointmentDetails", id);
      }

      // Step 5: Setting the order and format of the name
      if ("client_name_format" in json && "client_name_order" in json) {
        context.commit("setOrderAndFormatForGetAppointment", {
          clientNameFormat: json["client_name_format"],
          clientNameOrder: json["client_name_order"]
        });
      }
    }
  },

  /**
   * Delete a single appointment
   *
   * @param {Object} context
   * @param {Number} id
   */
  async deleteSingle(context, { id, deleteOrCancel }) {
    // Getting the selected provider id
    const selectedProviderId = context.getters.getSelectedProviderID;

    // Step 1: Check if id given is a number
    if (isNaN(id)) {
      return false;
    }

    // Step 2: Set the deleted to false,
    // to change page only when a response page is received
    context.commit("setDeleted", false);

    // Start spinner
    context.commit("setLoadingAppointmentDetails", true);

    // Step 3: Send DELETE request to delete the appointment
    const json = await context.dispatch("makeAuthRequest", {
      url: `appointment/delete/${id}/single/${deleteOrCancel}`,
      method: "DELETE"
    });

    // Stop spinner
    context.commit("setLoadingAppointmentDetails", false);

    if (!("error" in json)) {
      // Step 4: If successfully deleted
      // then remove appointment from appointment details and appointment list
      if (json.success === true) {
        const date =
          id in context.state.details
            ? context.state.details[id]["app_date"]
            : false;
        if (date) {
          if (deleteOrCancel === "cancel" || deleteOrCancel === "un-cancel") {
            context.commit("cancelFromDetails", {
              id,
              cancelOrUn: deleteOrCancel
            });
            context.commit("cancelFromAppointmentList", {
              date,
              id,
              cancelOrUn: deleteOrCancel
            });
          } else {
            context.commit("deleteFromDetails", id);
            context.commit("removeFromAppointmentList", {
              date,
              id
            });

            const year = parseInt(date.split("-")[0]);

            // Step 2: Refresh the years to check if last appointment has also been deleted
            context.commit("refreshYears", {
              years: [year],
              selectedProviderId
            });
          }
        }
      }

      // Step 6: Change deleted to true to change page
      context.commit("setDeleted", true);
    }

    if('Appointment can not be created for past time' === json.error) {
      context.dispatch(
        "globalMessagePopup",
        "Sorry! Video session can not be created for past time"
      );
    }
  },

  /**
   * Delete all future appointments
   *
   * @param {Object} context
   * @param {Number} id
   */
  async deleteFuture(context, { id, deleteOrCancel }) {
    // Getting the selected provider id
    const selectedProviderId = context.getters.getSelectedProviderID;

    // Step 1: Check if id given is a number
    if (isNaN(id)) {
      return false;
    }

    // Step 2: Set the deleted to false,
    // to change page only when a response page is received
    context.commit("setDeleted", false);

    // Start spinner
    context.commit("setLoadingAppointmentDetails", true);

    // Step 3: Send DELETE request to delete the appointment
    const json = await context.dispatch("makeAuthRequest", {
      url: `appointment/delete/${id}/future/${deleteOrCancel}`,
      method: "DELETE"
    });

    // Stop spinner
    context.commit("setLoadingAppointmentDetails", false);

    if (!("error" in json)) {
      // Step 4: Delete appointment from local
      if ("success" in json) {
        refresh.deleteRequired(
          json.success,
          context,
          selectedProviderId,
          deleteOrCancel
        );

        refresh.refreshRequired(
          json.success.map(res => res.app_date),
          context,
          selectedProviderId,
          false
        );
      }

      // Step 5: Change deleted to true to change page
      context.commit("setDeleted", true);
    }
  },

  /**
   * Delete all appointments
   *
   * @param {Object} context
   * @param {Number} id
   */
  async deleteAll(context, { id, deleteOrCancel }) {
    // Getting the selected provider id
    const selectedProviderId = context.getters.getSelectedProviderID;

    // Step 1: Check if id given is a number
    if (isNaN(id)) {
      return false;
    }

    // Step 2: Set the deleted to false,
    // to change page only when a response page is received
    context.commit("setDeleted", false);

    // Start spinner
    context.commit("setLoadingAppointmentDetails", true);

    // Step 3: Send DELETE request to delete the appointment
    const json = await context.dispatch("makeAuthRequest", {
      url: `appointment/delete/${id}/all/${deleteOrCancel}`,
      method: "DELETE"
    });

    // Stop spinner
    context.commit("setLoadingAppointmentDetails", false);

    if (!("error" in json)) {
      // Step 4: Delete appointment from local
      if ("success" in json) {
        refresh.deleteRequired(
          json.success,
          context,
          selectedProviderId,
          deleteOrCancel
        );
      }

      // Step 5: Change deleted to true to change page
      context.commit("setDeleted", true);
    }
  },

  /**
   * Going to edit appointment
   *
   * @param {Object} context
   * @param {Object} param1
   */
  async setEditAction(context, { id, appointment }) {
    // Start spinner
    context.commit("setLoadingAppointmentDetails", true);

    // Get form details, if it hasn't been fetched
    await context.dispatch("getFormDetails");

    // Set the edit details according to the appointment
    context.commit("setEdit", { id, appointment });

    // Stop spinner
    context.commit("setLoadingAppointmentDetails", false);

    // Change mode to edit
    context.commit("setChangeToEdit", true);
  }
};
